import { create } from 'zustand'

import { toast } from '@/shared/lib/toast'

import * as salesApi from '../api/sales.api'

import { useSalesDashboardStore } from './dashboard.store'

const initialState = {
  leads: [],
  meta: null,
  isLoading: false,
  isSubmitting: false,
  error: null,
}

export const useSalesLeadsStore = create((set) => ({
  ...initialState,

  clearError: () =>
    set({
      error: null,
    }),

  createLead: async (payload) => {
    if (useSalesLeadsStore.getState().isSubmitting) {
      return null
    }

    set({
      isSubmitting: true,
    })

    try {
      const result = await salesApi.createLead(payload)

      set((state) => ({
        leads: [result.data, ...state.leads],
        isSubmitting: false,
      }))

      toast.success('Lead created successfully')

      return result
    } catch (error) {
      const message = error.message || 'Failed to create lead'

      set({
        isSubmitting: false,
      })

      toast.error(message)
      throw error
    }
  },

  updateLead: async (leadId, payload) => {
    if (useSalesLeadsStore.getState().isSubmitting) {
      return null
    }

    set({
      isSubmitting: true,
    })

    try {
      const result = await salesApi.updateLead(leadId, payload)

      set((state) => ({
        leads: state.leads.map((lead) =>
          String(lead._id || lead.id) === String(leadId)
            ? { ...lead, ...(result.data || payload) }
            : lead,
        ),
        isSubmitting: false,
      }))

      toast.success(result.message || 'Lead updated successfully')

      return result
    } catch (error) {
      const message = error.message || 'Failed to update lead'

      set({
        isSubmitting: false,
      })

      toast.error(message)
      throw error
    }
  },

  fetchLeads: async (params = {}) => {
    set({
      isLoading: true,
      error: null,
    })

    try {
      const result = await salesApi.getAssignedLeads(params)

      set({
        leads: result.data,
        meta: result.meta,
        isLoading: false,
        error: null,
      })

      return result
    } catch (error) {
      const message = error.message || 'Failed to fetch assigned leads'

      set({
        isLoading: false,
        error: message,
      })

      toast.error(message)
      throw error
    }
  },

  updateLeadStatus: async (leadId, status) => {
    try {
      const result = await salesApi.updateLeadStatus(leadId, status)

      set((state) => ({
        leads: state.leads.map((lead) =>
          lead._id === leadId ? { ...lead, status: result.data.status } : lead,
        ),
      }))

      if (status === 'won' || status === 'lost' || status === 'closed') {
        useSalesDashboardStore.getState().removeLeadFromFollowedUp(leadId)
      }
      void useSalesDashboardStore.getState().fetchDashboard()

      toast.success('Lead status updated successfully')

      return result
    } catch (error) {
      const message = error.message || 'Failed to update lead status'

      toast.error(message)
      throw error
    }
  },

  sendLeadEmail: async (leadId, payload) => {
    try {
      const result = await salesApi.sendLeadEmail(leadId, payload)

      toast.success('Email sent successfully')

      return result
    } catch (error) {
      const message = error.message || 'Failed to send email'

      toast.error(message)
      throw error
    }
  },

  bulkUploadLeads: async (file) => {
    try {
      const result = await salesApi.bulkUploadLeads(file)

      toast.success('Leads uploaded successfully')

      return result
    } catch (error) {
      const message = error.message || 'Failed to upload leads'

      toast.error(message)
      throw error
    }
  },
}))
